import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCreateTagMutation, useTagsQuery } from '@platform/state/api';
import { TagInput as TagBox } from '@platform/ui';
import { routes } from '@platform/core/lib/routes';
import { tagColor } from '@platform/core/lib/statusColors';
import type { IControlProps } from '../IControlProps';

/** Відтінок нового тега (прототип createTag): (кількість тегів × 47 + 200) mod 360. */
const nextHue = (count: number): number => (count * 47 + 200) % 360;

/**
 * Теги запису (UIUX/field-types/tags, controls/tag-input): чипи з кольоровою крапкою, підказки зі словника воркспейсу, невідоме ім'я
 * нормалізується (малими, пробіли → «-») і створює тег у словнику (createTag) — значення лишається списком імен; «Керування» веде
 * до модуля «Теги». Лише читання — чипи без × або «Без тегів».
 */
export function TagInput({ id, value, onChange, readOnly, ws }: IControlProps<string[]>) {
  const { t } = useTranslation();
  const { data: all = [] } = useTagsQuery(ws);
  const [createTag] = useCreateTagMutation();
  const names = useMemo(() => value ?? [], [value]);
  const byName = useMemo(() => new Map(all.map((x) => [x.name.toLowerCase(), x])), [all]);
  const chips = names.map((n) => ({ key: n, label: n, color: byName.has(n.toLowerCase()) ? tagColor(byName.get(n.toLowerCase())!.hue) : undefined }));
  const tags = useMemo(() => all.map((x) => ({ key: x.name, label: x.name, color: tagColor(x.hue) })), [all]);
  const add = (name: string) => {
    if (!name || names.some((n) => n.toLowerCase() === name.toLowerCase())) return;
    const known = byName.get(name.toLowerCase());
    onChange([...names, known?.name ?? name]);
    if (!known) void createTag({ ws, body: { name, hue: nextHue(all.length) } });
  };
  return (
    <TagBox id={id} label={t('fieldControls.tagLabel')} chips={chips} tags={tags} onAdd={add} onRemove={(k) => onChange(names.filter((n) => n !== k))}
      readOnly={readOnly} placeholder={t('fieldControls.tagPlaceholder')} placeholderMore={t('fieldControls.tagPlaceholderMore')} emptyText={t('fieldControls.noTags')}
      manage={<Link className="pk-list__link" to={routes.module('tag')} onMouseDown={(e) => e.preventDefault()}>{t('fieldControls.manage')}</Link>} />
  );
}
